/**
 * Value formatting for the detail panels.
 *
 * Separate from the components that use it so that file exports components and
 * nothing else — Fast Refresh can only preserve a component's state when it can
 * be sure every export in the module is one.
 *
 * Most of what is here exists because the page aggregates. A panel field used
 * to hold one number from one location; now it holds whatever a scope of twelve
 * has to say, and the formatting is where "they agree" and "they do not" become
 * visible. A range that silently prints its average would make a scope split
 * between 3-day and 21-day lead times look settled.
 */

import type { ForecastDetailRange } from '@erp/rpc';

/** A percentage held to one decimal — service levels move in tenths. */
export function percent(value: number | null): string | null {
  return value === null ? null : `${(value * 100).toFixed(1)}%`;
}

/**
 * A setting across the scope: one number where every location agrees, and the
 * span where they do not.
 *
 * The span rather than the mean, because the mean is the one summary that can
 * be true of no location in the scope — and on lead time in particular, the
 * spread IS the finding.
 */
export function rangeLabel(
  value: ForecastDetailRange | null | undefined,
  format: (value: number) => string = (n) => String(Math.round(n * 10) / 10),
): string | null {
  if (!value) return null;
  if (value.min === value.max) return format(value.min);
  return `${format(value.min)}–${format(value.max)}`;
}

/** The same, for a range of percentages. */
export function percentRangeLabel(
  value: ForecastDetailRange | null | undefined,
): string | null {
  return rangeLabel(value, (n) => `${(n * 100).toFixed(1)}%`);
}

/**
 * A per-location label across the scope. One value prints itself; a few print
 * as a list; more than that prints a count, because a field five values wide
 * stops being readable and the panel is not where you enumerate them.
 */
export function setLabel(
  values: string[] | undefined,
  more: (count: number) => string,
): string | null {
  if (!values || values.length === 0) return null;
  if (values.length <= 3) return values.join(', ');
  return `${values.slice(0, 2).join(', ')}, ${more(values.length - 2)}`;
}

/**
 * The overflow phrase every panel hands to {@link setLabel}.
 *
 * Four panels each built their own copy of this closure, which is four places
 * for "+3 more" to drift into "+3 others". The translator call has to stay at
 * the call site — `gt` is a hook's return value — so the panel passes it in.
 */
export function moreLabel(
  gt: (message: string, values: Record<string, string>) => string,
): (count: number) => string {
  return (count) =>
    gt('+{count} more', {
      context: 'forecast detail, truncated value list',
      count: String(count),
    });
}

type Translate = (message: string, options: { context: string }) => string;

/**
 * A planner-readable name for a stored forecast method id.
 *
 * Keyed on the ids the monthly engine actually persists
 * (`ForecastMethod` in the backend's `forecasting/forecast.ts`, written to
 * `im_demand_forecasts.method`). Any other id prints as itself: a method this
 * page has never heard of is still a fact, and a guessed name would be a claim.
 */
export function forecastMethodLabel(method: string, gt: Translate): string {
  switch (method) {
    case 'moving_avg':
      return gt('12-month rolling average', {
        context: 'forecast method name for moving_avg',
      });
    case 'tsb':
      return gt('Intermittent demand average (TSB)', {
        context: 'forecast method name for tsb',
      });
    case 'holt_winters':
      return gt('Seasonal trend (Holt-Winters)', {
        context: 'forecast method name for holt_winters',
      });
    default:
      return method;
  }
}

/**
 * Stock status as the Y / N planners use: Y stocked, N non-stocked.
 *
 * Covers the classification contract (`stocked` / `non_stock`) and the legacy
 * GAINS single letters. Anything else prints as stored rather than being forced
 * into a yes or a no it may not mean.
 */
export function stockFlag(status: string): string {
  switch (status.trim().toLowerCase()) {
    case 'stocked':
    case 's':
    case 'y':
      return 'Y';
    case 'non_stock':
    case 'n':
      return 'N';
    default:
      return status;
  }
}

/** Distinct values after mapping, in first-seen order. */
export function distinctMapped(
  values: readonly string[] | undefined,
  map: (value: string) => string,
): string[] {
  return [...new Set((values ?? []).map(map))];
}

/**
 * A planner-readable name for a run's recorded demand basis. Unknown bases
 * print as stored, like methods.
 */
export function demandBasisLabel(basis: string, gt: Translate): string {
  switch (basis) {
    case 'legacy_outbound_v1':
      return gt('Outbound history (legacy)', {
        context: 'forecast demand basis name for legacy_outbound_v1',
      });
    case 'served_customer_sales_v1':
      return gt('Served customer sales', {
        context: 'forecast demand basis name for served_customer_sales_v1',
      });
    default:
      return basis;
  }
}

/**
 * Which resolved level a location's protection came from. Shared by the
 * planning levels and the surplus breakdown, which reads the same resolver.
 * Unknown sources print as stored.
 */
export function effectivePolicySourceLabel(
  source: string,
  gt: Translate,
): string {
  switch (source) {
    case 'min_floor':
      return gt('Minimum Floor', {
        context: 'stock surplus minimum protection source',
      });
    case 'forecast_safety_stock_override':
      return gt('Forecast · Safety Stock Override', {
        context: 'forecast detail, policy source',
      });
    case 'forecast_calculated':
      return gt('Forecast · Calculated Safety Stock', {
        context: 'forecast detail, policy source',
      });
    case 'fixed_min':
      return gt('Fixed minimum', { context: 'planning level source' });
    case 'min_override':
      return gt('Min Override', { context: 'forecast detail, policy source' });
    case 'min_calculated':
      return gt('Calculated Min', {
        context: 'forecast detail, policy source',
      });
    default:
      return source;
  }
}

/**
 * The quantity basis the surplus rule summed sales on. Unknown bases print as
 * stored: the basis is a fact the planner must see, not one to guess a name for.
 */
export function surplusSalesBasisLabel(basis: string, gt: Translate): string {
  // The served basis carries a human-readable suffix; its stable prefix names it.
  if (basis.startsWith('served_customer_sales_v1')) {
    return gt(
      'Signed customer shipment quantities; planner exclusion rules applied; buyer adjustments excluded',
      { context: 'stock surplus sales quantity basis' },
    );
  }
  switch (basis) {
    case 'unadjusted_customer_shipments':
      return gt('Customer shipments, unadjusted', {
        context: 'stock surplus sales quantity basis',
      });
    case 'customer_shipments':
      return gt('Customer shipments', {
        context: 'stock surplus sales quantity basis',
      });
    default:
      return basis;
  }
}

/**
 * Why customer sales cannot be shown, in a planner's words. Reason codes come
 * from the customer-sales reads; an unknown code prints as stored.
 */
export function customerSalesReasonLabel(
  reason: string,
  gt: Translate,
): string {
  switch (reason) {
    case 'no_transactions':
      return gt('No customer sales recorded in this window.', {
        context: 'forecast detail customer sales unavailable reason',
      });
    case 'source_overlap_conflict':
      return gt(
        'Two imports report the same orders without a shared line identity, so the sales cannot be counted once.',
        { context: 'forecast detail customer sales unavailable reason' },
      );
    case 'missing_source_identity':
      return gt(
        'Some sales lines are missing their source order or division, so they cannot be counted reliably.',
        { context: 'forecast detail customer sales unavailable reason' },
      );
    case 'source_incompatible':
      return gt(
        'Customer sales source unavailable: the imported lines cannot be confirmed as shipped customer sales (no recognized source, business division, or shipped quantity and date), so ordered quantities are not used in their place.',
        { context: 'forecast detail customer sales unavailable reason' },
      );
    default:
      return reason;
  }
}

/**
 * The derived block the Customer sales group reads, as the response types it.
 * Only `customerSales` is read: the top-level `derived.hits*` and
 * `avgDemandSize` keep their legacy monthly meaning and are never shown here,
 * not even as a fallback when customer sales are unavailable.
 */
interface DerivedSales {
  customerSales: {
    availability: 'available' | 'unavailable';
    availabilityReason: string | null;
    endMonth: string;
    hits6: number | null;
    hits9: number | null;
    hits12: number | null;
    priorYearHits: number | null;
    avgOrderSize: number | null;
  };
}

/** What the Customer sales group shows: values, or nulls before the read. */
export interface CustomerSalesView {
  hits6: number | null;
  hits9: number | null;
  hits12: number | null;
  priorYearHits: number | null;
  avgOrderSize: number | null;
  endMonth: string | null;
  /** Set only when sales are unavailable and the read said why. */
  unavailableReason: string | null;
}

export function customerSalesView(
  derived: DerivedSales | undefined,
): CustomerSalesView {
  if (!derived) {
    return {
      hits6: null,
      hits9: null,
      hits12: null,
      priorYearHits: null,
      avgOrderSize: null,
      endMonth: null,
      unavailableReason: null,
    };
  }
  const sales = derived.customerSales;
  return {
    hits6: sales.hits6,
    hits9: sales.hits9,
    hits12: sales.hits12,
    priorYearHits: sales.priorYearHits,
    avgOrderSize: sales.avgOrderSize,
    endMonth: sales.endMonth,
    unavailableReason:
      sales.availability === 'unavailable' ? sales.availabilityReason : null,
  };
}
